import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { styled } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BonusChip } from '@sorare/core/src/components/card/BonusChip';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { useDefaultAverageScore } from '@sorare/core/src/hooks/useDefaultAverageScore';

import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';
import { InjuryTag } from '#src/components/player/InjuryTag/index.tsx';

import type { AnyPlayerProperties_anyPlayer } from './__generated__/index.graphql.ts';

const Wrapper = styled(Horizontal)`
  flex-wrap: wrap;
  max-height: var(--quadruple-unit);
  overflow: hidden;
  & > * {
    flex: 0 0 auto;
  }
`;

type Props = {
  player: AnyPlayerProperties_anyPlayer;
  onStatsClick: () => void;
};

export const AnyPlayerProperties = ({ player, onStatsClick }: Props) => {
  const defaultAverageScore = useDefaultAverageScore();
  const score =
    player.sport === Sport.NBA
      ? player.lastTenPlayedSo5AverageScore
      : defaultAverageScore === 'LAST_TEN_PLAYED_SO5_AVERAGE_SCORE'
        ? player.lastTenPlayedSo5AverageScore
        : player.lastFifteenSo5AverageScore;

  return (
    <Wrapper>
      <AnyPlayerStatsIcon player={player} onClick={onStatsClick} />
      <Cost score={score} />
      <BonusChip bonus={1.05} />
      <InjuryTag player={player} size="small" />
    </Wrapper>
  );
};

AnyPlayerProperties.fragments = {
  anyPlayer: gql`
    fragment AnyPlayerProperties_anyPlayer on AnyPlayerInterface {
      slug
      sport
      ...InjuryTag_anyPlayer
      lastTenPlayedSo5AverageScore: averageScore(
        type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
      )
      lastFifteenSo5AverageScore: averageScore(
        type: LAST_FIFTEEN_SO5_AVERAGE_SCORE
      )
      ...AnyPlayerStatsIcon_anyPlayer
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${InjuryTag.fragments.anyPlayer}
  ` as TypedDocumentNode<AnyPlayerProperties_anyPlayer>,
};
